import Backbone from 'backbone';
import ReactDOM from 'react-dom';
import React from 'react';
import ko from 'knockout';
import { BackboneSchema as backboneSchema } from '@bingads-webui/backbone-schema';
import _ from 'underscore';
import $ from 'jquery';
import Promise from 'bluebird';
import { InputControl } from '@bingads-webui/input-control';
import { cloneDeep } from '@bingads-webui-universal/primitive-utilities';
import { DependencyResolver } from '@bingads-webui-campaign/dependency-resolver';
import { AudienceCampaignErrorsView, ImpressionTrackingUrlOptions } from '@bingads-webui-campaign/audience-campaign-shared';
import {
  ThirdPartyMeasurementBackboneView,
  getVerifiedTrackingSettingWrite,
  getVerifiedTrackingSettingsRead
} from '@bingads-webui-campaign-react/third-party-measurement';
import { RemainingCharsHint } from '@bingads-webui/remaining-chars-hint';
import { attachAdCustomizersHelpers, evaluateAdCustomizers } from '@bingads-webui-campaign/ad-customizers';
import { HotspotPanel, BoostAnchors } from '@bingads-webui-campaign-react/responsive-ad-editor-components';
import {
  attachAIGenAssetContextualAttribute,
} from '@bingads-webui-campaign/responsive-search-ad-strength';
import { CallToActionAriaText } from '@bingads-webui-campaign-react/legacy-call-to-action';

import template from './template.pug';
import editorHeadlineDescriptionOnlyTemplate from './editorHeadlineDescriptionOnly.pug';
import { MMAAssetConfigs, ResponsiveAdConfigs } from '../config';
import { attachAdStrengthSuggestionHelpers, clearAdStrengthSuggestionController, attachAdStrengthSuggestionHelperForURL } from '../asset-suggestion/asset-suggestion-helper';
import { headlineFields, longHeadlineFields, descriptionFields, ResponsiveAdScenarios, assetSuggestionType } from '../constants';
import { RecommendButton, urlChangeForRecommendationAd } from '../ad-recommend';
import { CallToActionText } from '../call-to-action-text/index.tsx';
import { getJobResults } from '../mma-creation-rsa/repository';

const CallToAction = ['Default', 'NoButton', 'ApplyNow', 'BookNow', 'ContactUs', 'Download', 'GetQuote', 'Install', 'LearnMore', 'SeeMore', 'ShopNow', 'SignUp', 'Subscribe', 'VisitSite', 'AddToCart', 'BookTravel', 'Buy', 'BuyNow', 'OpenLink', 'OrderNow', 'RegisterNow'];

/**
 * AdEdit class that extends {Backbone.View} class
 * The class defines the section for ad creation.
 *
 * @class AdEdit
 * @extends {Backbone.View}
 */
export class AdEdit extends Backbone.View {
  get events() {
    return {
      'click .save-ad-button': 'saveAdAsync',
      'click .ad-mma-add-headline': 'addHeadline',
      'click .ad-mma-add-long-headline': 'addLoneHeadline',
      'click .ad-mma-add-description': 'addDescription',
      'click .expand-cta-options': 'expandCTA',
      'click .expand-boost-anchors-options': 'toggleBoostAnchors',
      'click .headline-delete': 'removeHeadline',
      'click .long-headline-delete': 'removeLongHeadline',
      'click .description-delete': 'removeDescription',
      'focusout .final-url input.form-control.url-field': 'finalURLChange',
      'keyup .final-url input.form-control.url-value': 'finalURLChange',
      'change .option-select': 'adStatusChange',
      'click .errorCountLink': 'toggleErrorGrid',
      'click #runInBackground': 'hideProgress',
    };
  }

  /**
   * Initialize the ad image manager.
   *
   * @param {object} options - options, further defined bellow.
   *
   * @param {object} options.viewContext - viewContext object in order to get the campaign and ad
   * group ids.
   *
   * @param {object} options.ad - ad to be edited.
   *
   * @param {boolean} options.isUpdate - determines if the ad creation editor should create a new ad
   * or update existing one.
   *
   * @param {boolean} options.isGridDisplay - determine if the save button needs to be shown or not.
   *
   * @returns {undefined} no return value
   *
   */
  initialize(options = {}) {
    this.isForVNext = options.isForVNext || false;
    this.isUpdate = this.isAdNotEmpty(options.ad) && (options.ad.isSaved !== false);
    this.campaignSettings = options.campaignSettings;
    this.ad = _.omit(options.ad, v => (typeof v !== 'number' && (this.isEmpty(v) || v[0] == null)));
    this.showSaveButton = options.isGridDisplay || false;
    this.logScenario = options.logScenario;
    this.baseURL = options.baseURL;
    this.isSimplifyAudienceCreation = options.isSimplifyAudienceCreation;
    this.isAdRecommendationMSANEnabled = options.isAdRecommendationMSANEnabled;
    this.newI18n = options.newI18n;
    this.i18n = DependencyResolver.get('component/i18n/loader!campaignui/feature/audience-campaign');
    this.i18nErrors = DependencyResolver.get('component/api-errors-campaign/index');
    this.config = DependencyResolver.get('component/config/index');
    this.permissions = this.config.get('Permissions') || {};
    this.odata = DependencyResolver.get('component/http/channel/odata');
    this.recommendButtonReactComponent = React.createRef();
    this.hotspotReactComponent = React.createRef();
    this.boostAnchorsReactComponent = React.createRef();
    this.callToActionTextComponent = React.createRef();
    this.callToActionAriaTextComponent = React.createRef();
    this.instrumentation = options.instrumentation;
    this.scenario = options.scenario;
    this.edm = options.edm;
    this.jsdata = options.jsdata;
    this.isVideo = options.isVideo;
    this.isMobileUrlEnabled = options.isMobileUrlEnabled == null ?
      true : options.isMobileUrlEnabled;
    this.viewContext = options.viewContext;
    this.pageContext = options.pageContext;
    this.isImpressionTrackingUrlsEnabled =
      (this.viewContext &&
        (_.result(this.viewContext.currentCampaign, 'CampaignType') === 1 ||
          _.result(this.viewContext.currentCampaign, 'CampaignType') === 'Default')) ||
        options.isImpressionTrackingUrlsEnabled == null ?
        false : options.isImpressionTrackingUrlsEnabled;

    this.biddingSchemeType = this.getBiddingSchemeType(options);
    this.isThirdPartyMeasurementEnabled =
      (_.result(this.viewContext.currentCampaign, 'CampaignType') === 'Audience' ||
        _.result(this.viewContext.currentCampaign, 'CampaignType') === 8) &&
      (_.result(this.viewContext.currentCampaign, 'CampaignSubType') === 'AudienceStatic' ||
        _.result(this.viewContext.currentCampaign, 'CampaignSubType') === 0);
    this.isMOATEnabled = _.get(this.permissions, 'IsMOATEnabled', false);

    this.ImpressionTrackingUrlsMaxCount =
      this.permissions.IsUpToSixImpressionTrackingUrlsEnabled ? 6 : 2;

    this.isAudienceCampaign = options.isAudienceCampaign;
    this.isMSANLogoAndCTAForMMAEnabled = options.isMSANLogoAndCTAForMMAEnabled;
    this.isMultimediaAd = options.isMultimediaAd;
    this.isBulkEditMMA = options.isBulkEditMMA;

    this.isMultiMediaAdsPhase2Enabled =
      this.isMultimediaAd && this.permissions.IsMultiMediaAdsPhase2Enabled;
    this.isMMAAutomatedCTAOptInEnabled =
      this.isMultimediaAd && this.permissions.IsMMAAutomatedCTAOptInEnabled;
    this.callToActionSetting = this.campaignSettings && _.find(this.campaignSettings, setting => setting['@odata.type'] === '#Model.CallToActionSetting');
    this.automatedCTAEnable = !this.callToActionSetting || !this.callToActionSetting.AutomatedCallToActionOptOut;

    this.isAudienceCampaignMultiMediaAdsEnabled =
      this.permissions.IsAudienceCampaignMultiMediaAdsEnabled &&
      (_.result(this.viewContext.currentCampaign, 'CampaignType') === 'Audience' ||
        _.result(this.viewContext.currentCampaign, 'CampaignType') === 8) &&
      !options.isVideo;

    this.isBoostScenario = this.isAudienceCampaign && this.permissions.IsAmplifyEnabled;
    this.isBoostCTAAriaTextEnabled = this.isBoostScenario && this.permissions.IsBoostCTAAriaTextEnabled;
    this.isBoostAnchorsEnabled = this.isBoostScenario && this.permissions.IsBoostAnchorEnabled;
    this.isBoostCustomCTAEnabled = this.permissions.IsBoostCustomCTAEnabled;

    this.isMMAAdCustomizersEnabled = this.permissions.IsMMACountdownInsertionEnabled ||
      this.permissions.IsMMAKeywordInsertionEnabled ||
      this.permissions.IsMMALocationInsertionEnabled ||
      this.permissions.IsMMAAdCustomizerInsertionEnabled;

    this.IsBoostCTAPhase2Enabled = this.permissions.IsBoostCTAPhase2Enabled;

    this.isMSANBoostEnabled = this.isAudienceCampaign && this.permissions.IsAmplifyEnabled;
    this.responsiveAdScenario = ResponsiveAdScenarios.NonMMAResponsive;

    if (this.isMultiMediaAdsPhase2Enabled) {
      this.responsiveAdScenario = ResponsiveAdScenarios.MMASearch;
    }

    if (this.isAudienceCampaignMultiMediaAdsEnabled) {
      this.responsiveAdScenario = ResponsiveAdScenarios.MMAResponsive;
    }

    this.responsiveAdScenario = `${this.responsiveAdScenario} | IsMMAAutoFillEnabled: ${this.permissions.IsMMAAutoFillEnabled}`;

    this.isMMAEnabled =
      this.isMultiMediaAdsPhase2Enabled || this.isAudienceCampaignMultiMediaAdsEnabled;

    this.isMoveFinalUrlTop = this.isAudienceCampaignMultiMediaAdsEnabled || this.isMultimediaAd;

    this.activityLogger = options.activityLogger;
    this.genericErr = new AudienceCampaignErrorsView();

    if (this.ad.Headlines && typeof (this.ad.Headlines) === 'string') {
      this.ad.Headlines = JSON.parse(this.ad.Headlines);
    }
    if (this.ad.LongHeadlines && typeof (this.ad.LongHeadlines) === 'string') {
      this.ad.LongHeadlines = JSON.parse(this.ad.LongHeadlines);
    }
    if (this.ad.Descriptions && typeof (this.ad.Descriptions) === 'string') {
      this.ad.Descriptions = JSON.parse(this.ad.Descriptions);
    }

    this.setUpAdWithRecommendAd = options.setUpAdWithRecommendAd;
    this.shouldOverrideAdInEditor = options.shouldOverrideAdInEditor;
    this.isSimplifyAudienceCreation = options.isSimplifyAudienceCreation;

    this.isMMAV2Enabled = options.isMMAV2Enabled;

    this.initHeadlines = this.ad.Headlines ? this.ad.Headlines.length : 0;
    this.initLongHeadlines = this.ad.LongHeadlines ? this.ad.LongHeadlines.length : 0;
    this.initDescriptions = this.ad.Descriptions ? this.ad.Descriptions.length : 0;

    this.preferencesService = DependencyResolver.get('component/preference/service/index');
    this.websiteImageOptInPreferenceName = options.websiteImageOptInPreferenceName;
    this.hasWebsiteImageOptedInObservable = options.hasWebsiteImageOptedInObservable;
    this.parsedTextMaxLengthExtension = options.parsedTextMaxLengthExtension;
    this.onUpdateHotspots = options.onUpdateHotspots;
    this.hotspots = options.hotspots;
    this.boostAnchorData = _.first(this.ad.BoostAnchors);
    this.callToActionText = this.ad && !_.isEmpty(this.ad.CallToActionText) ? this.ad.CallToActionText : null;
    this.callToActionAriaText = this.ad && !_.isEmpty(this.ad.CallToActionAriaText) ? this.ad.CallToActionAriaText : null;

    if (options.isBulkEditMMA) {
      this.adStatusOptions = [
        {
          text: this.i18n.getString('Active'),
          value: 'Active',
        },
        {
          text: this.i18n.getString('Paused'),
          value: 'Paused',
        },
      ];
      this.onClose = options.onClose;
      this.showProgress = ko.observable(false);
      this.statusText = ko.observable(this.i18n.getString('BulkEdit_InitialStatus'));
      this.successCount = ko.observable(0);
      this.errorCount = ko.observable(0);
      this.totalCount = ko.observable(0);
      this.adStatus = ko.observable('Active');
      this.completePercentage = ko.computed(() => {
        const total = this.totalCount();
        if (total === 0) {
          return '1%';
        }

        const success = this.successCount();
        const error = this.errorCount();
        const completed = Math.floor(((success + error) * 100) / total);
        return completed < 1 ? `${completed + 1}%` : `${completed}%`;
      });
      // Error related properties
      this.maxInlineErrorCount = 100;
      this.showErrors = ko.observable(false);
      this.showErrorGrid = ko.observable(false);
      this.errorCountString = ko.computed(() => this.i18n.getString('BulkEdit_InlineError_ErrorCount').replace('{0}', this.errorCount()));
      this.initInlineErrors = ko.observable([]);
      this.inlineErrors = ko.computed({
        read() {
          return this.initInlineErrors();
        },
        write(value) {
          /* istanbul ignore next */
          if (value.Status === 'CompletedWithErrors') {
            // due to the repository is js can not build success in demo
            getJobResults(value.Id, 'errors', 0, this.maxInlineErrorCount, 'Ad', this.odata)
              .done((response) => {
                if (response && response.value) {
                  const err = response.value.map((errorObject) => {
                    const errorKey = errorObject.Errors[0];
                    const entityName = errorObject.EntityKey[0].Value;

                    return {
                      error: this.i18n.getString(`Error_Campaign_API_${errorKey}`),
                      name: entityName,
                    };
                  });

                  this.initInlineErrors(err);
                }
                this.viewModel.showProgress(false);
                this.viewModel.showErrors(true);
              });
          }
        },
      }, this);
      this.errorCountLinkText = ko.computed(() => {
        if (this.showErrorGrid()) {
          return this.i18n.getString('BulkEdit_InlineError_HideErrorLink');
        }
        return this.i18n.getString('BulkEdit_InlineError_ErrorLink').replace('{0}', this.errorCount());
      }, this);
      this.errorsGrids = ko.observable([
        this.i18n.getString(_TL_('Ad')),
        this.i18n.getString(_TL_('Error')),
      ], this);
    }

    this.isImproveMMA = options.isImproveMMA;
    this.isRecommendation = options.isRecommendation;

    const currentOptInPref =
      this.preferencesService.findByNameAtUserLevel(this.websiteImageOptInPreferenceName);

    const shouldCTANotExpand = this.isBoostScenario && !_.get(this.ad, 'CallToActionLanguage');

    this.viewModel = {
      maxHeadlines: MMAAssetConfigs.MAX_HEADLINES,
      showHeadlineCount: ko.observable(Math.max(
        MMAAssetConfigs.DEFAULT_HEADLINES,
        this.initHeadlines
      )),
      headlinesErrorMessage: ko.observable(false),
      maxLongHeadlines: MMAAssetConfigs.MAX_LONGHEADLINES,
      showLongHeadlineCount: ko.observable(Math.max(
        MMAAssetConfigs.DEFAULT_LONGHEADLINES,
        this.initLongHeadlines
      )),
      longHeadlinesErrorMessage: ko.observable(false),
      maxDescriptions: MMAAssetConfigs.MAX_DESCRIPTIONS,
      showDescriptionCount: ko.observable(Math.max(
        MMAAssetConfigs.DEFAULT_DESCRIPTIONS,
        this.initDescriptions
      )),
      maxHeadlineSuggestions: this.isMMAEnabled ?
        MMAAssetConfigs.MAX_HEADLINE_SUGGESTIONS :
        ResponsiveAdConfigs.MAX_HEADLINE_SUGGESTIONS,
      maxDescriptionSuggestions: this.isMMAEnabled ?
        MMAAssetConfigs.MAX_DESCRIPTION_SUGGESTIONS :
        ResponsiveAdConfigs.MAX_DESCRIPTION_SUGGESTIONS,
      currentSuggestions: [],
      autoFillHeadlineCount: 2,
      autoFillLongHeadlineCount: 1,
      autoFillDescriptionCount: 2,
      descriptionsErrorMessage: ko.observable(false),
      businessNameErrorMessage: ko.observable(false),
      isCTAExpanded: ko.observable(!shouldCTANotExpand),
      isBoostAnchorsExpanded: ko.observable(this.ad && !_.isEmpty(this.ad.BoostAnchors)),
      Language: ko.observable(this.ad && !_.isEmpty(this.ad.CallToActionLanguage) ?
        this.ad.CallToActionLanguage : null),
      actionLanguageErrorMessage: ko.observable(false),
      ActionType: ko.observable(this.ad && !_.isEmpty(this.ad.CallToAction) ?
        this.ad.CallToAction : null),
      actionTextErrorMessage: ko.observable(false),
      urlOptionErrorMessage: ko.observable(false),
      adGroupId: Number(this.viewContext.currentAdGroup.Id),
      campaignId: Number(this.viewContext.currentCampaign.Id),
      showWebsiteImageOptIn: ko.observable(false),
      hasWebsiteImageOptIn:
        ko.observable(currentOptInPref && currentOptInPref.Data && currentOptInPref.Data.OptIn),
    };
    if (options.isBulkEditMMA) {
      _.extend(this.viewModel, {
        showProgress: this.showProgress,
        statusText: this.statusText,
        completePercentage: this.completePercentage,
        adStatusOptions: this.adStatusOptions,
        adStatus: this.adStatus,
        showErrors: this.showErrors,
        showErrorGrid: this.showErrorGrid,
        errorCount: this.errorCount,
        errorCountString: this.errorCountString,
        errorCountLinkText: this.errorCountLinkText,
        inlineErrors: this.inlineErrors,
        errorsGrids: this.errorsGrids,
        maxInlineErrorCount: this.maxInlineErrorCount,
      });
    }

    this.initializeAdModel();
    this.initializeAdContent();
    if (this.ad && this.ad.errors) {
      this.initializeAdError();
    }

    this.viewModel.hasWebsiteImageOptIn.subscribe((newOptInState) => {
      this.preferencesService.setAtUserLevel({
        Name: this.websiteImageOptInPreferenceName,
        Data: { OptIn: newOptInState },
      });
      this.hasWebsiteImageOptedInObservable(newOptInState);
    });

    this.activityLogger.signal(`ad-edit view initialize: ${this.permissions.dynamic && this.permissions.dynamic.MMACopilot}`, 'rae-view deprecation');
  }

  clear() {
    this.isUpdate = false;
    this.ad = null;
    this.initializeAdModel();
    this.initializeAdContent();
    ko.cleanNode(this.el);
    this.render();
  }

  initializeAdModel() {
    const schema = cloneDeep(DependencyResolver.get('$/schema!campaign/ResponsiveAd'));
    const fields = [
      'Campaign',
      'AdGroup',
      !this.isMMAEnabled && 'Headline',
      !this.isMMAEnabled && 'LongHeadline',
      'BusinessName',
      !this.isMMAEnabled && 'Text',
      'FinalUrls',
      this.isMobileUrlEnabled && 'MobileFinalUrls',
      'TrackingUrlTemplate',
      'FinalUrlSuffix',
      'UrlCustomParameters',
      'CallToAction',
      (this.isMultiMediaAdsPhase2Enabled || this.isBoostScenario || this.isMSANLogoAndCTAForMMAEnabled) && 'CallToActionLanguage',
      'Images',
      this.isImpressionTrackingUrlsEnabled && 'ImpressionTrackingUrls',
      this.isThirdPartyMeasurementEnabled && 'VerifiedTrackingData',
    ];

    // Temporary override of the maxLength. Once everything is ready, schema itself will be updated
    schema.properties.Headline.maxLength = 30;

    if (this.isMMAEnabled) {
      this.headlineFields = headlineFields;
      _.each(this.headlineFields, (key) => {
        schema.properties[key] = _.clone(schema.properties.Headline);
      });
      fields.push(this.headlineFields);

      if (this.ad) {
        if (this.initHeadlines === 0) {
          // create before phase2, fill Headline to Headline0
          this.ad.Headline0 = this.ad.Headline;
        } else {
          _.each(_.get(this.ad, ['Headlines'], []), (val, idx) => {
            this.ad[this.headlineFields[idx]] = _.isEmpty(val.Asset) ? val.Text : val.Asset.Text;
          });
        }
      }

      this.longHeadlineFields = longHeadlineFields;
      _.each(this.longHeadlineFields, (key) => {
        schema.properties[key] = _.clone(schema.properties.LongHeadline);
      });
      fields.push(this.longHeadlineFields);

      if (this.ad) {
        if (this.initLongHeadlines === 0) {
          // create before phase2, fill LongHeadline to LongHeadline0
          this.ad.LongHeadline0 = this.ad.LongHeadline;
        } else {
          _.each(_.get(this.ad, ['LongHeadlines'], []), (val, idx) => {
            this.ad[this.longHeadlineFields[idx]] = _.isEmpty(val.Asset) ?
              val.Text : val.Asset.Text;
          });
        }
      }

      this.descriptionFields = descriptionFields;
      _.each(this.descriptionFields, (key) => {
        schema.properties[key] = _.clone(schema.properties.Text);
      });
      fields.push(this.descriptionFields);

      if (this.ad) {
        if (this.initDescriptions === 0) {
          // create before phase2, fill Text to Description0
          this.ad.Description0 = this.ad.Text;
        } else {
          _.each(_.get(this.ad, ['Descriptions'], []), (val, idx) => {
            this.ad[this.descriptionFields[idx]] = _.isEmpty(val.Asset) ? val.Text : val.Asset.Text;
          });
        }
      }
      if (this.isBulkEditMMA) {
        fields.push('AdStatus');
      }
    }

    // For Boost only, we got updated CallToActionLanguage to support
    if (this.isBoostScenario) {
      if (this.IsBoostCTAPhase2Enabled) {
        if (_.result(schema, ['properties', 'CallToActionLanguage'])) {
          schema.properties.CallToActionLanguage.enum = _.union(
            schema.properties.CallToActionLanguage.enum,
            [
              'Albanian',
              'Catalan',
              'Gaelic',
              'Hindi',
              'Indonesian',
              'Korean',
              'Thai',
              'Ukrainian',
              'Vietnamese',
              'Afrikaans',
              'Amharic',
              'Armenian',
              'Assamese',
              'Azerbaijani',
              'Basque',
              'Bengali',
              'Cambodian',
              'Galician',
              'Georgian',
              'Gujarati',
              'Kannada',
              'Kazakh',
              'Laotian',
              'Malay',
              'Malayalam',
              'Maori',
              'Marathi',
              'Nepali',
              'Oriya',
              'Persian',
              'Punjabi',
              'Quechua',
              'Tamil',
              'Telugu',
              'Urdu',
              'Uzbek',
              'Welsh',
            ]
          );
        }
      } else if (_.result(schema, ['properties', 'CallToActionLanguage'])) {
        schema.properties.CallToActionLanguage.enum = _.union(
          schema.properties.CallToActionLanguage.enum,
          [
            'Albanian',
            'Catalan',
            'Gaelic',
            'Hindi',
            'Indonesian',
            'Korean',
            'Thai',
            'Ukrainian',
            'Vietnamese',
          ]
        );
      }
    }

    const SchemaModel = backboneSchema(
      schema,
      this.newI18n || this.i18n,
      _.flatten(_.compact(fields)),
      this.ad
    );

    this.model = new SchemaModel();
    this.model.set({
      CallToAction: this.ad && !_.isEmpty(this.ad.CallToAction) ? this.ad.CallToAction : 'Default',
    });

    if (this.isMultiMediaAdsPhase2Enabled || this.isBoostScenario || this.isMSANLogoAndCTAForMMAEnabled) {
      this.model.set({
        CallToActionLanguage: this.ad && !_.isEmpty(this.ad.CallToActionLanguage) ?
          this.ad.CallToActionLanguage : null,
      });
    }

    if (this.isThirdPartyMeasurementEnabled) {
      if (this.ad) {
        const verifiedTrackingSettings = getVerifiedTrackingSettingsRead(this.ad);
        this.model.set(verifiedTrackingSettings);
      }
    }

    if (this.isBulkEditMMA) {
      this.model.set({
        AdStatus: this.adStatus(),
      });
    }

    this.viewModel.model = this.model;
  }

  enableAdStrengthSuggestionHelpers() {
    const enableAdStrengthFields = _.union(this.headlineFields, this.longHeadlineFields, this.descriptionFields); // eslint-disable-line max-len
    _.each(enableAdStrengthFields, (field) => {
      const element = this[field].$el;
      const property = this[field];
      attachAdCustomizersHelpers({
        container: $('div[data-type="Responsive"], div.crud-multimedia-ad-react'),
        element,
        updateAction: (text) => {
          property.entity.set(field, text);
          $(`.form-control.${field}-value`).val(text);
          property.trigger('input-changed');
        },
        deps: {
          i18n: this.i18n,
          countdownI18nDummy: this.i18n,
          config: this.config,
          instrumentation: this.instrumentation,
          permissions: this.permissions,
          edmCampaign: this.edm,
          jsDataCampaign: this.jsdata,
          scope: {
            campaignId: Number(this.viewContext.currentCampaign.Id),
            adGroupId: Number(this.viewContext.currentAdGroup.Id),
          },
        },
        adType: 'ResponsiveAd',
        field,
      });
    });
    if (this.parsedTextMaxLengthExtension) {
      this.parsedTextMaxLengthExtension.extend(this, enableAdStrengthFields, 'ResponsiveAd');
    }
  }

  getEvaluateAdCustomizers(text) {
    return new Promise((resolve) => {
      if (this.isMMAAdCustomizersEnabled) {
        evaluateAdCustomizers({
          text,
          deps: {
            i18n: this.i18n,
            countdownI18nDummy: this.i18n,
            config: this.config,
            instrumentation: this.instrumentation,
            jsDataCampaign: this.jsdata,
          },
          adType: 'ResponsiveAd',
        })
          .then((data) => {
            resolve(data);
          });
      } else {
        resolve(text);
      }
    });
  }

  overrideEvents(elem, field) {
    elem.delegateEvents({
      [`input .${field}-value`]: (event) => {
        elem.entity.set(field, event.target.value);
        elem.trigger('input-changed', event);
        elem.setState({ showError: true });
      },
      [`focus .${field}-value`]: (event) => {
        elem.trigger('input-focused', event);
      },
    });
  }

  /**
   * Initialize the form part of the ad creation view.
   *
   * @returns {undefined} no return value
   *
   */
  initializeAdContent() {
    clearAdStrengthSuggestionController();
    if (this.isMMAEnabled) {
      _.each(this.headlineFields, (field) => {
        this[field] = new InputControl({
          entity: this.model,
          field,
          ariaLabelledby: 'responsiveAdShortHeadlineLabel',
        });

        this.overrideEvents(this[field], field);

        this[field].on('input-changed', () => {
          const data = this.model.toJSON();
          const firstHeadline = _.first(_.filter(
            this.headlineFields,
            key => !_.isEmpty(data[key])
          ));
          this.getEvaluateAdCustomizers(data[firstHeadline])
            .then((text) => {
              const updateObject = {
                updatedElement: 'ShortHeadline',
                text,
              };
              this.activityLogger.signal(`ad field change: ${field}`, 'responsive ad editor');
              this.trigger('fieldUpdated', updateObject);
              $(`.form-control.${field}-value`).click();
            });
          this.updateAIGenAssetContextualAttributeStatus(field, 'none');
        });

        this[`${field}RemainingCharsHint`] = new RemainingCharsHint({
          i18n: this.i18n,
          property: this[field],
          model: this.model,
          field,
        });

        this[field].maxLength = () => this[`${field}RemainingCharsHint`].maxSize;

        if (!this.isRecommendation) {
          attachAdStrengthSuggestionHelpers({
            element: this[field],
            field,
            viewModel: this.viewModel,
            pageContext: this.pageContext,
            assetType: assetSuggestionType.Headline,
            i18n: this.i18n,
            activityLogger: this.activityLogger,
            scenario: this.responsiveAdScenario,
            isRecommendation: this.isRecommendation,
          });
        }
      });

      _.each(this.longHeadlineFields, (field) => {
        this[field] = new InputControl({
          entity: this.model,
          field,
          ariaLabelledby: 'responsiveAdLongHeadlineLabel',
        });

        this.overrideEvents(this[field], field);

        this[field].on('input-changed', () => {
          const data = this.model.toJSON();
          const firstLongHeadline = _.first(_.filter(
            this.longHeadlineFields,
            key => !_.isEmpty(data[key])
          ));
          this.getEvaluateAdCustomizers(data[firstLongHeadline])
            .then((text) => {
              const updateObject = {
                updatedElement: 'LongHeadline',
                text,
              };
              this.activityLogger.signal(`ad field change: ${field}`, 'responsive ad editor');
              this.trigger('fieldUpdated', updateObject);
              $(`.form-control.${field}-value`).click();
            });
          this.updateAIGenAssetContextualAttributeStatus(field, 'none');
        });

        this[`${field}RemainingCharsHint`] = new RemainingCharsHint({
          i18n: this.i18n,
          property: this[field],
          model: this.model,
          field,
        });

        this[field].maxLength = () => this[`${field}RemainingCharsHint`].maxSize;

        if (!this.isRecommendation) {
          attachAdStrengthSuggestionHelpers({
            element: this[field],
            field,
            viewModel: this.viewModel,
            pageContext: this.pageContext,
            assetType: assetSuggestionType.LongHeadline,
            i18n: this.i18n,
            activityLogger: this.activityLogger,
            scenario: this.responsiveAdScenario,
            isRecommendation: this.isRecommendation,
          });
        }
      });

      _.each(this.descriptionFields, (field) => {
        this[field] = new InputControl({
          entity: this.model,
          field,
          ariaLabelledby: 'responsiveAdDescriptionLabel',
        });

        this.overrideEvents(this[field], field);

        this[field].on('input-changed', () => {
          const data = this.model.toJSON();
          const firstDescription = _.first(_.filter(
            this.descriptionFields,
            key => !_.isEmpty(data[key])
          ));
          this.getEvaluateAdCustomizers(data[firstDescription])
            .then((text) => {
              const updateObject = {
                updatedElement: 'Description',
                text,
              };
              this.activityLogger.signal(`ad field change: ${field}`, 'responsive ad editor');
              this.trigger('fieldUpdated', updateObject);
              $(`.form-control.${field}-value`).click();
            });
          this.updateAIGenAssetContextualAttributeStatus(field, 'none');
        });

        this[`${field}RemainingCharsHint`] = new RemainingCharsHint({
          i18n: this.i18n,
          property: this[field],
          model: this.model,
          field,
        });

        this[field].maxLength = () => this[`${field}RemainingCharsHint`].maxSize;

        if (!this.isRecommendation) {
          attachAdStrengthSuggestionHelpers({
            element: this[field],
            field,
            viewModel: this.viewModel,
            pageContext: this.pageContext,
            assetType: assetSuggestionType.Description,
            i18n: this.i18n,
            activityLogger: this.activityLogger,
            scenario: this.responsiveAdScenario,
            isRecommendation: this.isRecommendation,
          });
        }
      });

      if (this.isMultiMediaAdsPhase2Enabled || this.isBoostScenario || this.isMSANLogoAndCTAForMMAEnabled) {
        const ActionLanguageAdapter = DependencyResolver.get('campaignui/feature/adextension/actionlink/view/language-adapter');
        this.actionLanguageAdapter = new ActionLanguageAdapter({
          observable: this.viewModel.Language,
          viewModel: this.viewModel,
          entity: this.model,
          field: 'CallToActionLanguage',
          isMultimediaAd: this.isMultimediaAd || this.isMSANLogoAndCTAForMMAEnabled,
          isMMAAutomatedCTAOptInEnabled: this.isMMAAutomatedCTAOptInEnabled,
          isBoostScenario: this.isBoostScenario,
          callback: (selectedOption) => {
            this.model.set('CallToActionLanguage', selectedOption.id);
            this.trigger('fieldUpdated', {
              updatedElement: 'CallToActionLanguage',
              text: selectedOption.value,
            });
          },
        });

        const ActionTypeAdapter = DependencyResolver.get('campaignui/feature/adextension/actionlink/view/action-type-adapter');
        this.actionTypeAdapter = new ActionTypeAdapter({
          observable: this.viewModel.ActionType,
          viewModel: this.viewModel,
          entity: this.model,
          field: 'CallToAction',
          isMultimediaAd: this.isMultimediaAd || this.isMSANLogoAndCTAForMMAEnabled,
          isMMAAutomatedCTAOptInEnabled: this.isMMAAutomatedCTAOptInEnabled,
          isBoostScenario: this.isBoostScenario,
          isBoostCustomCTAEnabled: this.isBoostCustomCTAEnabled,
          callback: (selectedOption) => {
            this.model.set('CallToAction', selectedOption.id);
            this.trigger('fieldUpdated', {
              updatedElement: 'CallToAction',
              text: selectedOption.value,
            });
          },
        });
      }
    } else {
      this.shortHeadline = new InputControl({
        entity: this.model,
        field: 'Headline',
        ariaLabelledby: 'responsiveAdShortHeadlineLabel',
      });

      this.shortHeadline.on('input-changed', () => {
        const updateObject = {
          updatedElement: 'ShortHeadline',
          text: this.shortHeadline.getState().value,
        };
        this.activityLogger.signal('ad field change: shortHeadline', 'responsive ad editor');
        this.trigger('fieldUpdated', updateObject);
      });

      this.overrideEvents(this.shortHeadline, 'Headline');

      if (!this.isRecommendation) {
        attachAdStrengthSuggestionHelpers({
          element: this.shortHeadline,
          field: 'Headline',
          viewModel: this.viewModel,
          pageContext: this.pageContext,
          assetType: assetSuggestionType.Headline,
          i18n: this.i18n,
          activityLogger: this.activityLogger,
          scenario: this.responsiveAdScenario,
          isRecommendation: this.isRecommendation,
        });
      }

      this.shortHeaderRemainingCharsHint = new RemainingCharsHint({
        i18n: this.i18n,
        model: this.model,
        field: 'Headline',
      });

      this.longHeadline = new InputControl({
        entity: this.model,
        field: 'LongHeadline',
        ariaLabelledby: 'responsiveAdLongHeadlineLabel',
      });

      this.overrideEvents(this.longHeadline, 'LongHeadline');

      if (!this.isRecommendation) {
        attachAdStrengthSuggestionHelpers({
          element: this.longHeadline,
          field: 'LongHeadline',
          viewModel: this.viewModel,
          pageContext: this.pageContext,
          assetType: assetSuggestionType.LongHeadline,
          i18n: this.i18n,
          activityLogger: this.activityLogger,
          scenario: this.responsiveAdScenario,
          isRecommendation: this.isRecommendation,
        });
      }

      this.longHeaderRemainingCharsHint = new RemainingCharsHint({
        i18n: this.i18n,
        model: this.model,
        field: 'LongHeadline',
      });

      this.longHeadline.on('input-changed', () => {
        const updateObject = {
          updatedElement: 'LongHeadline',
          text: this.longHeadline.getState().value,
        };
        this.activityLogger.signal('ad field change: longHeadline', 'responsive ad editor');
        this.trigger('fieldUpdated', updateObject);
      });

      this.description = new InputControl({
        entity: this.model,
        field: 'Text',
        ariaLabelledby: 'responsiveAdDescriptionLabel',
      });

      this.overrideEvents(this.description, 'Text');

      if (!this.isRecommendation) {
        attachAdStrengthSuggestionHelpers({
          element: this.description,
          field: 'Text',
          viewModel: this.viewModel,
          pageContext: this.pageContext,
          assetType: assetSuggestionType.Text,
          i18n: this.i18n,
          activityLogger: this.activityLogger,
          scenario: this.responsiveAdScenario,
          isRecommendation: this.isRecommendation,
        });
      }

      this.descriptionRemainingCharsHint = new RemainingCharsHint({
        i18n: this.i18n,
        model: this.model,
        field: 'Text',
      });

      this.description.on('input-changed', () => {
        const updateObject = {
          updatedElement: 'Description',
          text: this.description.getState().value,
        };
        this.activityLogger.signal('ad field change: description', 'responsive ad editor');
        this.trigger('fieldUpdated', updateObject);
      });
    }

    this.businessName = new InputControl({
      entity: this.model,
      field: 'BusinessName',
      ariaLabelledby: 'responsiveAdBusinessNameLabel',
    });
    this.businessName.on('input-changed', () => {
      const updateObject = {
        updatedElement: 'BusinessName',
        text: this.businessName.getState().value,
      };
      this.activityLogger.signal('ad field change: businessName', 'responsive ad editor');
      this.trigger('fieldUpdated', updateObject);
    });

    this.overrideEvents(this.businessName, 'BusinessName');

    if (!this.isRecommendation) {
      attachAdStrengthSuggestionHelpers({
        element: this.businessName,
        field: 'BusinessName',
        viewModel: this.viewModel,
        pageContext: this.pageContext,
        assetType: assetSuggestionType.BusinessName,
        i18n: this.i18n,
        activityLogger: this.activityLogger,
        scenario: this.responsiveAdScenario,
        isRecommendation: this.isRecommendation,
      });
    }

    this.businessNameRemainingCharsHint = new RemainingCharsHint({
      i18n: this.i18n,
      model: this.model,
      field: 'BusinessName',
    });

    const UrlEditor = DependencyResolver.get('component/url-input/view/url-input');

    this.finalUrlEditor = new UrlEditor({
      inputProtocol: this.isMSANBoostEnabled,
      url: _.first(_.result(this.model.attributes, 'FinalUrls')),
      ariaLabelledby: 'responsiveAdFinalUrlLabel',
      ariaId: 'responsiveAdFinalUrl',
      disabled: this.scenario === 'new-audience-static-creation',
      required: true,
    });

    this.mobileUrlEditor = new UrlEditor({
      inputProtocol: this.isMSANBoostEnabled,
      url: _.first(_.result(this.model.attributes, 'MobileFinalUrls')),
      ariaLabelledby: 'responsiveAdMobileUrlLabel',
      ariaId: 'responsiveAdMobileUrl',
    });

    if (this.isImpressionTrackingUrlsEnabled) {
      this.ImpressionTrackingUrlsUrlOptions = new ImpressionTrackingUrlOptions({
        i18n: this.i18n,
        initialImpressionTrackingUrls: this.model.get('ImpressionTrackingUrls'),
        maxImpressionTrackingUrlsCount: this.ImpressionTrackingUrlsMaxCount,
        isIASSectionVisible: false,
      });

      this.ImpressionTrackingUrlsUrlOptions.on('InputChange', (impressionTrackingUrls) => {
        this.model.errors.unset('ImpressionTrackingUrl');
        this.model.set('ImpressionTrackingUrls', impressionTrackingUrls);
        this.ImpressionTrackingUrlsUrlOptions.removeErrors();
      });
    }

    if (this.isThirdPartyMeasurementEnabled) {
      this.ThirdPartyMeasurementView = new ThirdPartyMeasurementBackboneView({
        i18n: this.i18n,
        initialThirdPartyMeasurement: this.model.get('VerifiedTrackingSettings'),
        isMOATEnabled: this.isMOATEnabled,
      });

      this.ThirdPartyMeasurementView.on('InputChange', (thirdPartyMeasurement) => {
        this.model.set('VerifiedTrackingSettings', thirdPartyMeasurement);
      });

      this.ThirdPartyMeasurementView.listenTo(this.model, 'change:VerifiedTrackingSettings', (model) => {
        this.ThirdPartyMeasurementView.renderWithInput(model.get('VerifiedTrackingSettings'));
      });
    }

    this.finalUrlEditor.on('urlChanged', (url) => {
      this.activityLogger.signal('ad field change: finalUrls', 'responsive ad editor');
      if (url) {
        this.model.set({ FinalUrls: [url] });
        this.finalUrlEditor.setError('');
        this.trigger('fieldUpdated', { updatedElement: 'FinalUrl', url });
      } else {
        this.model.set({ FinalUrls: [] });
        this.trigger('fieldUpdated', { updatedElement: 'FinalUrl', url: '' });
      }
    });

    if (this.isAdRecommendationMSANEnabled && this.isAudienceCampaign && !this.isVideo && !this.isSimplifyAudienceCreation) {
      urlChangeForRecommendationAd(this.finalUrlEditor, this.recommendButtonReactComponent);
    }

    if (!this.isRecommendation) {
      this.onUrlChanged = attachAdStrengthSuggestionHelperForURL({
        element: this.finalUrlEditor,
        viewModel: this.viewModel,
        pageContext: this.pageContext,
        view: this,
        i18n: this.i18n,
        activityLogger: this.activityLogger,
        scenario: this.responsiveAdScenario,
      });
    }

    this.mobileUrlEditor.on('urlChanged', (url) => {
      this.activityLogger.signal('ad field change: mobileFinalUrls', 'responsive ad editor');
      this.model.set({ MobileFinalUrls: url ? [url] : [] });
      if (url) {
        this.mobileUrlEditor.setError('');
      }
    });

    const UrlOptions = DependencyResolver.get('component/upgraded-url/view/urlOptionsV2');

    this.advancedURLOptions = new UrlOptions({
      entityType: 'Audience',
      placeholderEnabled: true,
      trackingTemplate: _.result(this.model.attributes, 'TrackingUrlTemplate'),
      finalUrlSuffix: _.result(this.model.attributes, 'FinalUrlSuffix'),
      customParameters: _.isEmpty(_.result(this.model.attributes, 'UrlCustomParameters')) ? undefined : _.clone(this.model.attributes.UrlCustomParameters),
      showUurlTestTool: true,
      isForVNext: this.isForVNext,
      activityLogger: this.activityLogger,
    });

    this.advancedURLOptions.on('testUurl', (fn, originalView) => {
      const testUurl = DependencyResolver.get('component/upgraded-url/service/testUurl');
      const state = DependencyResolver.get('component/state/index');
      originalView.isInline = false; // eslint-disable-line
      originalView.level = testUurl.uurlLevels.Ad; // eslint-disable-line
      const params = this.getData() || {};
      const context = {
        customerId: state.get('customer_id'),
        userId: state.get('user_id'),
        accountId: state.get('account_id'),
        campaignId: state.get('campaign_id') || (params.Campaign && params.Campaign.Id) || 0,
        adGroupId: state.get('adgroup_id') || (params.AdGroup && params.AdGroup.Id) || 0,
        entityId: params.Id ? params.Id : '',
      };
      const options = {
        apiInLog: 'responsive-ad-editor/uurlTest',
      };
      testUurl.testUurl(fn, originalView, params, context, options);
    });

    if (this.isUpdate &&
      (!this.isEmpty(this.ad.TrackingUrlTemplate) || !this.isEmpty(this.ad.UrlCustomParameters) ||
        !this.isEmpty(this.ad.FinalUrlSuffix))) {
      this.advancedURLOptions.expand();
    }

    this.adPropertiesToControlMap = {
      FinalUrls: this.finalUrlEditor,
      MobileFinalUrls: this.mobileUrlEditor,
    };
  }

  finalURLChange() {
    if (typeof this.onUrlChanged === 'function') {
      this.onUrlChanged();
    }
  }

  adStatusChange(e) {
    this.adStatus(e.target.value);
  }

  toggleErrorGrid() {
    this.showErrorGrid(!this.showErrorGrid());
  }

  hideProgress() {
    this.showProgress(false);
    this.onClose();
  }

  initializeAdError() {
    _.forEach(this.ad.errors, (error) => {
      this.setError(error);
    });
  }

  updateFinalUrlPrefix(prefix) {
    this.finalUrlEditor.ko_model.protocol(prefix);
  }

  updateFinalUrl(url) {
    this.finalUrlEditor.ko_model.fullUrl(url);
  }

  addHeadline(e) {
    // remove focus style after click
    e.currentTarget.blur();
    if (this.viewModel.showHeadlineCount() < MMAAssetConfigs.MAX_HEADLINES) {
      this.viewModel.showHeadlineCount(this.viewModel.showHeadlineCount() + 1);

      this.activityLogger.signal(`headline clicked: MMAinMSAN ${this.isAudienceCampaignMultiMediaAdsEnabled}`, 'responsive ad editor');
    }
  }

  addLoneHeadline(e) {
    // remove focus style after click
    e.currentTarget.blur();
    if (this.viewModel.showLongHeadlineCount() < MMAAssetConfigs.MAX_LONGHEADLINES) {
      this.viewModel.showLongHeadlineCount(this.viewModel.showLongHeadlineCount() + 1);
      this.activityLogger.signal(`long headline clicked: MMAinMSAN ${this.isAudienceCampaignMultiMediaAdsEnabled}`, 'responsive ad editor');
    }
  }

  addDescription(e) {
    // remove focus style after click
    e.currentTarget.blur();
    if (this.viewModel.showDescriptionCount() < MMAAssetConfigs.MAX_DESCRIPTIONS) {
      this.viewModel.showDescriptionCount(this.viewModel.showDescriptionCount() + 1);
      this.activityLogger.signal(`description clicked: MMAinMSAN ${this.isAudienceCampaignMultiMediaAdsEnabled}`, 'responsive ad editor');
    }
  }

  expandCTA() {
    this.viewModel.isCTAExpanded(!this.viewModel.isCTAExpanded());
  }

  toggleBoostAnchors() {
    this.viewModel.isBoostAnchorsExpanded(!this.viewModel.isBoostAnchorsExpanded());
  }

  removeHeadline(e) {
    const index = Number(e.currentTarget.dataset.id);
    this.shiftOneIndex('Headline', index, this.viewModel.showHeadlineCount());
    this.viewModel.showHeadlineCount(this.viewModel.showHeadlineCount() - 1);
  }

  removeLongHeadline(e) {
    const index = Number(e.currentTarget.dataset.id);
    this.shiftOneIndex('LongHeadline', index, this.viewModel.showLongHeadlineCount());
    this.viewModel.showLongHeadlineCount(this.viewModel.showLongHeadlineCount() - 1);
  }

  removeDescription(e) {
    const index = Number(e.currentTarget.dataset.id);
    this.shiftOneIndex('Description', index, this.viewModel.showDescriptionCount());
    this.viewModel.showDescriptionCount(this.viewModel.showDescriptionCount() - 1);
  }

  // elements of fields shift one index to the left
  shiftOneIndex(type, index, count) {
    let field = `${type}${index}`;
    let nextField;

    if (_.some(this.viewModel.currentSuggestions, suggestion =>
      suggestion.Text === this[field].getState().value)) {
      this.activityLogger.signal(`${field}, ${this[field].getState().value}`, `${this.responsiveAdScenario} delete a suggested asset`);
    } else {
      this.activityLogger.signal(`${field}, ${this[field].getState().value}`, `${this.responsiveAdScenario} delete an asset`);
    }

    // remove last one
    if (index >= count - 1) {
      if (this[field]) {
        this[field].setState({
          value: '',
        });
        this.updateAIGenAssetContextualAttributeStatus(field, 'none');
      }
      return;
    }

    for (let i = index; i < count - 1; i += 1) {
      nextField = `${type}${i + 1}`;

      const { value } = this[nextField].getState();
      const display = this.getAIGenAssetContextualAttributeStatus(nextField);

      this[field].setState({
        value,
      });
      this.updateAIGenAssetContextualAttributeStatus(field, display);

      field = nextField;
    }

    if (this[nextField]) {
      this[nextField].setState({
        value: '',
      });
      this.updateAIGenAssetContextualAttributeStatus(nextField, 'none');
    }
  }

  /**
   * Renders the view
   *
   * @returns {Object} view itself
   */
  render() {
    if (this.isImproveMMA) {
      this.$el.html(editorHeadlineDescriptionOnlyTemplate({
        i18n: this.i18n,
        headlineFields: this.headlineFields,
        longHeadlineFields: this.longHeadlineFields,
        descriptionFields: this.descriptionFields,
      }));
    } else if (this.isBulkEditMMA) {
      this.$el.html(template({
        i18n: this.i18n,
        callToAction: CallToAction,
        showSaveButton: this.showSaveButton,
        isImpressionTrackingUrlsEnabled: this.isImpressionTrackingUrlsEnabled,
        isMultimediaAd: this.isMultimediaAd,
        isMultiMediaAdsPhase2Enabled: this.isMultiMediaAdsPhase2Enabled,
        isMMAAutomatedCTAOptInEnabled: this.isMMAAutomatedCTAOptInEnabled,
        automatedCTAEnable: this.automatedCTAEnable,
        isBoostScenario: this.isBoostScenario,
        isMSANLogoAndCTAForMMAEnabled: this.isMSANLogoAndCTAForMMAEnabled,
        isMMAEnabled: this.isMMAEnabled,
        isMoveFinalUrlTop: this.isMoveFinalUrlTop,
        isBulkEditMMA: this.isBulkEditMMA,
        headlineFields: this.headlineFields,
        longHeadlineFields: this.longHeadlineFields,
        descriptionFields: this.descriptionFields,
        showProgress: this.showProgress,
        statusText: this.statusText,
        completePercentage: this.completePercentage,
        adStatusOptions: this.adStatusOptions,
        showErrors: this.showErrors,
        errorCountString: this.errorCountString,
        inlineErrors: this.inlineErrors,
        errorsGrids: this.errorsGrids,
        errorCountLinkText: this.errorCountLinkText,
      }));
    } else {
      this.$el.html(template({
        i18n: this.i18n,
        callToAction: CallToAction,
        showSaveButton: this.showSaveButton,
        isImpressionTrackingUrlsEnabled: this.isImpressionTrackingUrlsEnabled,
        isMultimediaAd: this.isMultimediaAd,
        isMultiMediaAdsPhase2Enabled: this.isMultiMediaAdsPhase2Enabled,
        isMMAAutomatedCTAOptInEnabled: this.isMMAAutomatedCTAOptInEnabled,
        automatedCTAEnable: this.automatedCTAEnable,
        isBoostScenario: this.isBoostScenario,
        isMSANLogoAndCTAForMMAEnabled: this.isMSANLogoAndCTAForMMAEnabled,
        isBoostAnchorsEnabled: this.isBoostAnchorsEnabled,
        isBoostCustomCTAEnabled: this.isBoostCustomCTAEnabled,
        isBoostCTAAriaTextEnabled: this.isBoostCTAAriaTextEnabled,
        isMMAEnabled: this.isMMAEnabled,
        isMoveFinalUrlTop: this.isMoveFinalUrlTop,
        isBulkEditMMA: this.isBulkEditMMA,
        headlineFields: this.headlineFields,
        longHeadlineFields: this.longHeadlineFields,
        descriptionFields: this.descriptionFields,
      }));
    }
    ko.applyBindings(this.viewModel, this.el);

    if (this.isImproveMMA && this.isMMAEnabled) {
      _.each(this.headlineFields, (field, idx) => {
        this.$(`.short-headline-form-${idx} .short-headline-form`).append(this[field].render().el);
        this.$(`.short-headline-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });
      _.each(this.longHeadlineFields, (field, idx) => {
        this.$(`.long-headline-form-${idx} .long-headline-form`).append(this[field].render().el);
        this.$(`.long-headline-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });
      _.each(this.descriptionFields, (field, idx) => {
        this.$(`.description-form-${idx} .description-form`).append(this[field].render().el);
        this.$(`.description-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });

      this.enableAIGenAssetContextualAttribute();

      return this;
    }

    if (this.isMMAEnabled) {
      _.each(this.headlineFields, (field, idx) => {
        this.$(`.short-headline-form-${idx} .short-headline-form`).append(this[field].render().el);
        this.$(`.short-headline-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });
      _.each(this.longHeadlineFields, (field, idx) => {
        this.$(`.long-headline-form-${idx} .long-headline-form`).append(this[field].render().el);
        this.$(`.long-headline-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });
      _.each(this.descriptionFields, (field, idx) => {
        this.$(`.description-form-${idx} .description-form`).append(this[field].render().el);
        this.$(`.description-form-${idx} > .remaining-chars-hint-container`).append(this[`${field}RemainingCharsHint`].render().el);
      });
      if (this.isMultiMediaAdsPhase2Enabled || this.isBoostScenario || this.isMSANLogoAndCTAForMMAEnabled) {
        this.$('.action-language').append(this.actionLanguageAdapter.render().el);
        this.$('.action-text').append(this.actionTypeAdapter.render().el);
      }
    } else {
      this.$('.short-headline-form').append(this.shortHeadline.render().el);
      this.$('.short-headline > .remaining-chars-hint-container').append(this.shortHeaderRemainingCharsHint.render().el);
      this.$('.long-headline-form').append(this.longHeadline.render().el);
      this.$('.long-headline > .remaining-chars-hint-container').append(this.longHeaderRemainingCharsHint.render().el);
      this.$('.description-form').append(this.description.render().el);
      this.$('.description > .remaining-chars-hint-container').append(this.descriptionRemainingCharsHint.render().el);
    }

    this.$('.business-name-form').append(this.businessName.render().el);
    this.$('.business-name > .remaining-chars-hint-container').append(this.businessNameRemainingCharsHint.render().el);
    this.$('.final-url').append(this.finalUrlEditor.render().el);
    if (this.isMobileUrlEnabled) {
      this.$('.mobile-url').append(this.mobileUrlEditor.render().el);
    } else {
      this.$('.mobile-url').remove();
    }

    if (this.isImpressionTrackingUrlsEnabled) {
      this.$('.impression-tracking-urls').append(this.ImpressionTrackingUrlsUrlOptions.render().el);
    }

    if (this.isThirdPartyMeasurementEnabled) {
      this.$('.third-party-measurements').append(this.ThirdPartyMeasurementView.render().el);
    }

    this.$('.advanced-url-options').append(this.advancedURLOptions.render().el);
    this.$('.generic-errors').append(this.genericErr.render().el);

    if (this.isMMAAdCustomizersEnabled) {
      this.enableAdStrengthSuggestionHelpers();
      if (this.ad) {
        const enableAdStrengthFields = _.union(this.headlineFields, this.longHeadlineFields, this.descriptionFields); // eslint-disable-line max-len
        _.each(enableAdStrengthFields, (field) => {
          this[`${field}RemainingCharsHint`].updateHint();
        });
      }
    }

    if (this.isAdRecommendationMSANEnabled && this.isAudienceCampaign && !this.isVideo && !this.isSimplifyAudienceCreation) {
      ReactDOM.render(<RecommendButton
        model={this.model}
        baseURL={this.baseURL}
        ref={this.recommendButtonReactComponent}
        setUpAdWithRecommendAd={this.setUpAdWithRecommendAd}
        activityLogger={this.activityLogger}
        i18n={this.newI18n}
        shouldOverrideAdInEditor={this.shouldOverrideAdInEditor}
        isMMAV2Enabled={this.isMMAV2Enabled}
      />, this.$('.get-recommend-ad')[0]);
    }

    if (this.isMSANBoostEnabled) {
      ReactDOM.render(
        <HotspotPanel
          ref={this.hotspotReactComponent}
          i18n={this.newI18n}
          onUpdateHotspots={this.onUpdateHotspots}
          hotspots={this.hotspots}
          enableHotSpotPlacement={this.permissions.IsBoostHotSpotPlacementEnabled}
        />,
        this.$('.hotspot-panel')[0]
      );
    }

    if (this.isBoostAnchorsEnabled) {
      ReactDOM.render(
        <BoostAnchors
          i18n={this.newI18n}
          defaultSelectedKey={this.boostAnchorData}
          ref={this.boostAnchorsReactComponent}
        />,
        this.$('.boost-anchors-section')[0]
      );
    }

    if (this.isBoostScenario && this.isBoostCustomCTAEnabled) {
      ReactDOM.render(
        <CallToActionText
          i18n={this.newI18n}
          defaultValue={this.callToActionText}
          ref={this.callToActionTextComponent}
        />,
        this.$('.call-to-action-text')[0]
      );
    }

    if (this.isBoostCTAAriaTextEnabled) {
      ReactDOM.render(
        <CallToActionAriaText
          i18n={this.newI18n}
          value={this.callToActionAriaText}
          ref={this.callToActionAriaTextComponent}
        />,
        this.$('.call-to-action-aria-text')[0]
      );
    }

    return this;
  }

  /**
   * Returns a boolean that determines whether the passed variable is empty or not.
   * Empty meaning: null, undefined, []
   *
   * @param {object} value The value to be inspected.
   *
   * @returns {boolean} True for empty value, false otherwise.
   *
   */
  isEmpty(value) {
    // value == null also handles the case where value is undefined
    return (value == null || value.length === 0);
  }

  /**
   * Makes sure that all the needed parameters are contained on the object, and valid, before this
   * is used to save the ad.
   *
   * @param {object} params The object to be sent on the save request.
   *
   * @returns {boolean} True if everything is ok, false if values are missing or invalid.
   *
   */
  isDataValid(params) {
    let isValid = true;

    if (this.isMMAEnabled) {
      if (this.isImproveMMA) {
        this.viewModel.actionLanguageErrorMessage(false);
        this.viewModel.actionTextErrorMessage(false);
      } else if (this.isMultiMediaAdsPhase2Enabled) {
        if (_.isEmpty(params.CallToActionLanguage)) {
          isValid = false;
          this.viewModel.actionLanguageErrorMessage(this.i18n.getString('Validation_NotEmpty_Required_Field'));
        } else {
          this.viewModel.actionLanguageErrorMessage(false);
        }

        if (_.isEmpty(params.CallToAction) || params.CallToAction === 'Default') {
          isValid = false;
          this.viewModel.actionTextErrorMessage(this.i18n.getString('Validation_NotEmpty_Required_Field'));
        } else {
          this.viewModel.actionTextErrorMessage(false);
        }
      }
      if (this.isBulkEditMMA) {
        if (this.isEmpty(params.BusinessName) || params.BusinessName.length > MMAAssetConfigs.MAX_BUSINESS_NAME) {
          isValid = false;
          this.businessName.setState({ showError: true });
          return false;
        }
        return isValid;
      }
      if (_.isEmpty(params.Headlines) ||
        JSON.parse(params.Headlines).length < MMAAssetConfigs.MIN_HEADLINES) {
        isValid = false;
        this.viewModel.headlinesErrorMessage(this.i18n.getString('Multimedia_Ad_Error_AssetFieldMinimumNotMet', {
          field: this.i18n.getString('Audience_Campaign_Ad_Short_Headline_Label').toLowerCase(),
          min_count: MMAAssetConfigs.MIN_HEADLINES,
        }));
      } else {
        this.viewModel.headlinesErrorMessage(false);
      }
      // longheadline optional in MMA
      if ((((_.isEmpty(params.LongHeadlines) ||
        JSON.parse(params.LongHeadlines).length < MMAAssetConfigs.MIN_LONGHEADLINES))) && (!this.isMMAEnabled || this.isAudienceCampaign)) {
        isValid = false;
        this.viewModel.longHeadlinesErrorMessage(this.i18n.getString('Multimedia_Ad_Error_AssetFieldMinimumNotMet', {
          field: this.i18n.getString('Audience_Campaign_Ad_Long_Headline_Label').toLowerCase(),
          min_count: MMAAssetConfigs.MIN_LONGHEADLINES,
        }));
      } else {
        this.viewModel.longHeadlinesErrorMessage(false);
      }

      if (_.isEmpty(params.Descriptions) ||
        JSON.parse(params.Descriptions).length < MMAAssetConfigs.MIN_DESCRIPTIONS) {
        isValid = false;
        this.viewModel.descriptionsErrorMessage(this.i18n.getString('Multimedia_Ad_Error_AssetFieldMinimumNotMet', {
          field: this.i18n.getString('Audience_Campaign_Ad_Description_Label').toLowerCase(),
          min_count: MMAAssetConfigs.MIN_DESCRIPTIONS,
        }));
      } else {
        this.viewModel.descriptionsErrorMessage(false);
      }

      this.viewModel.businessNameErrorMessage(false);
      this.viewModel.urlOptionErrorMessage(false);
    }

    if (!this.isImproveMMA) {
      if (this.isEmpty(params.FinalUrls)) {
        isValid = false;
        this.finalUrlEditor.setError(this.i18n.getString('Audience_Campaign_Ad_Error_RequiredField'));
      }
    }

    if (!this.model.isValid()) {
      if (!this.isMMAEnabled) {
        this.shortHeadline.setState({ showError: true });
        this.longHeadline.setState({ showError: true });
        this.description.setState({ showError: true });
      }
      if (this.businessName) {
        this.businessName.setState({ showError: true });
      }
      return false;
    }

    if (this.isMSANBoostEnabled) {
      isValid = isValid && this.hotspotReactComponent.current.validate();

      if (this.isBoostCustomCTAEnabled && this.model.get('CallToAction') === 'CustomText') {
        isValid = isValid && this.callToActionTextComponent.current.isValid();
      }

      if (this.isBoostCTAAriaTextEnabled) {
        isValid = isValid && this.callToActionAriaTextComponent.current.isValid();
      }
    }

    // isValid = isValid && this.advancedURLOptions.isValid();
    return isValid;
  }

  /**
   * Check if the error is AdEdit error, if so set the error message
   *
   * @param {object} error - Error object
   * @returns {boolean} - true if the error belongs to the AdEdit false other wise.
   */
  setError(error) {
    if (error.Property && _.includes(_.keys(this.adPropertiesToControlMap), error.Property)) {
      const control = this.adPropertiesToControlMap[error.Property];

      control.setError(this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`));
      return true;
    }
    let ifCatch = 0;
    if (error.Error && error.Error.toLowerCase().indexOf('impressiontrackingurl') !== -1) {
      const errors = this.model.errors.get('ImpressionTrackingUrl') instanceof Array ?
        this.model.errors.get('ImpressionTrackingUrl').concat(this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`)) :
        [this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`)];

      this.model.errors.set({
        ImpressionTrackingUrl: errors,
      });
      this.ImpressionTrackingUrlsUrlOptions.setErrors(errors);

      ifCatch = 1;
    }

    if (error.Property === 'CallToAction') {
      this.viewModel.actionLanguageErrorMessage(this.i18n.getString(`Error_Campaign_API_${error.Error}`));
    }

    if (error.Property === 'ResponsiveAdHeadlines') {
      this.viewModel.headlinesErrorMessage(this.i18n.getString(`Multimedia_Ad_Error_${error.Error}`, {
        field: 'short headline',
        min_count: MMAAssetConfigs.MIN_HEADLINES,
      }) || this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`));
    }

    if (error.Property === 'ResponsiveAdLongHeadlines') {
      this.viewModel.longHeadlinesErrorMessage(this.i18n.getString(`Multimedia_Ad_Error_${error.Error}`, {
        field: 'long headline',
        min_count: MMAAssetConfigs.MIN_LONGHEADLINES,
      }) || this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`));
    }

    if (error.Property === 'ResponsiveAdDescriptions') {
      this.viewModel.descriptionsErrorMessage(this.i18n.getString(`Multimedia_Ad_Error_${error.Error}`, {
        field: 'ad text',
        min_count: MMAAssetConfigs.MIN_DESCRIPTIONS,
      }) || this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`));
    }

    if (error.Property === 'BusinessName') {
      this.viewModel.descriptionsErrorMessage(this.i18n.getString(`Multimedia_Ad_Error_${error.Error}`));
    }

    if (error.Property === 'CustomParameters' || (error.Error && error.Error.toLowerCase().indexOf('customparameters') !== -1)) {
      this.viewModel.urlOptionErrorMessage(this.i18nErrors.getString(`Error_Campaign_API_${error.Error}`));
    }

    if (_.isEmpty(error.Property) && !ifCatch) {
      const genericErrorMessage = this.i18nErrors.getString(`API_ErrorNumber_${error.ErrorNumber}`)
        || this.i18n.getString(`Multimedia_Ad_Error_${error.Error}`)
        || error.message
        || this.i18nErrors.getString('ErrorNumber_-1');

      this.genericErr.setErrorMessage(genericErrorMessage);
    }

    if (error.Error === 'InvalidUrlScheme') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('Invalid Url Schema')));
    }

    if (error.Error === 'HotSpotMissingRequiredField') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('HotSpot Missing Required Field')));
    }

    if (error.Error === 'InvalidGlyph') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('Invalid Glyph')));
    }

    if (error.Error === 'HotSpotsHasDuplication') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('HotSpots Has Duplication')));
    }

    if (error.Error === 'CustomerIsNotAllowedForHotSpots') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('Customer Is Not Allowed For HotSpots')));
    }

    if (error.Error === 'InvalidHotSpot') {
      this.hotspotReactComponent.current.setError(this.i18n.getString(_TL_('Invalid HotSpot')));
    }


    return false;
  }

  /**
   * Sets the input elements on the ad model, and returns an object with the parameters
   * needed for the save request.
   *
   * @returns {object} Object with parameters to save the ad.
   *
   */
  getData() {
    const TrackingUrlTemplate = this.advancedURLOptions.getTrackingTemplate();
    const UrlCustomParameters = this.advancedURLOptions.getUrlCustomParameters(true);
    const FinalUrlSuffix = this.advancedURLOptions.getFinalUrlSuffix();
    this.model.set({
      TrackingUrlTemplate,
      UrlCustomParameters,
      FinalUrlSuffix,
    });

    if (this.isThirdPartyMeasurementEnabled) {
      const verifiedTrackingSettings = this.model.get('VerifiedTrackingSettings');
      const verifiedTrackingSettingsAttachment = getVerifiedTrackingSettingWrite(verifiedTrackingSettings);
      this.model.set(verifiedTrackingSettingsAttachment);
    }

    let data = this.model.toJSON();
    const adGroupId = Number(this.viewContext.currentAdGroup.Id);
    const campaignId = Number(this.viewContext.currentCampaign.Id);

    if (this.isBoostScenario) {
      if (data.CallToAction === 'Default') {
        delete data.CallToAction;
        delete data.CallToActionLanguage;
      }
    }

    if (this.isMSANBoostEnabled) {
      data.HotSpots = JSON.stringify(this.hotspotReactComponent.current.getData());
    }

    if (this.isBoostAnchorsEnabled) {
      data.BoostAnchors = this.boostAnchorsReactComponent.current.getData();
    }

    if (this.isBoostScenario && this.isBoostCustomCTAEnabled) {
      data.CallToActionText = this.callToActionTextComponent.current.getData();
    }

    if (this.isBoostCTAAriaTextEnabled) {
      data.CallToActionAriaText = this.callToActionAriaTextComponent.current.getData();
    }

    _.extend(data, { Campaign: { Id: campaignId }, AdGroup: { Id: adGroupId } });

    if (this.isUpdate) {
      data.Id = this.ad.Id;
      data = this.deleteUndefinedParameters(data);
    }

    if (this.isMMAEnabled) {
      data.Headlines = JSON.stringify(_.map(_.filter(
        this.headlineFields,
        field => !_.isEmpty(data[field])
      ), field => ({
        Text: data[field],
      })));
      data.LongHeadlines = JSON.stringify(_.map(_.filter(
        this.longHeadlineFields,
        field => !_.isEmpty(data[field])
      ), field => ({
        Text: data[field],
      })));
      data.Descriptions = JSON.stringify(_.map(_.filter(
        this.descriptionFields,
        field => !_.isEmpty(data[field])
      ), field => ({
        Text: data[field],
      })));
      if (this.isBulkEditMMA) {
        data.AdStatus = this.adStatus() || '';
      }
    }

    return _.omit(data, this.isEmpty);
  }

  /**
   * Set the optional parameters that are set to emty, to be 'delete_value', so that they get
   * deleted during the ad update.
   *
   * @param {object} params The object to be sent on the update request.
   *
   * @returns {object} Updated object to be sent to the update request.
   *
   */
  deleteUndefinedParameters(params) {
    const optionalParameters = ['MobileFinalUrls', 'TrackingUrlTemplate', 'UrlCustomParameters', 'LandscapeLogoMediaId', 'SquareLogoMediaId', 'FinalUrlSuffix'];

    if (this.isImpressionTrackingUrlsEnabled) {
      optionalParameters.push('ImpressionTrackingUrls');
    }

    if (this.isMMAEnabled) {
      optionalParameters.push(...[
        'Headline',
        'LongHeadline',
        'Text',
      ]);
    }

    if (this.isThirdPartyMeasurementEnabled) {
      optionalParameters.push('VerifiedTrackingData');
    }

    _.forEach(optionalParameters, (p) => {
      if (this.isEmpty(params[p]) || (params[p] instanceof Array && params[p].length === 0)) {
        // eslint-disable-next-line no-param-reassign
        params[p] = 'delete_value';
      }
    });

    return params;
  }

  isAdNotEmpty(ad) {
    return _.get(ad, 'Id', -1) > 0;
  }

  /**
   * Registers AI generated asset contextual attribute popup to listen for key/mouse events
   * @returns {undefined}
   */
  enableAIGenAssetContextualAttribute() {
    const self = this;

    _.each(_.range(this.viewModel.showHeadlineCount()), (i) => {
      const element = self.$(`.iconba.iconba-Lightbulb.Headline${i}-AIGeneratedAsset`);
      _.first(element).style.display = this.ad.Headlines[i].IsAlgoGeneratedAsset ? 'inline-block' : 'none';
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n: this.i18n,
        improveMMAOption: { type: 'Text' },
        newI18n: this.newI18n,
      }));
    });

    _.each(_.range(this.viewModel.showLongHeadlineCount()), (i) => {
      const element = self.$(`.iconba.iconba-Lightbulb.LongHeadline${i}-AIGeneratedAsset`);
      _.first(element).style.display = this.ad.LongHeadlines[i].IsAlgoGeneratedAsset ? 'inline-block' : 'none';
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n: this.i18n,
        improveMMAOption: { type: 'Text' },
        newI18n: this.newI18n,
      }));
    });

    _.each(_.range(this.viewModel.showDescriptionCount()), (i) => {
      const element = self.$(`.iconba.iconba-Lightbulb.Description${i}-AIGeneratedAsset`);
      _.first(element).style.display = this.ad.Descriptions[i].IsAlgoGeneratedAsset ? 'inline-block' : 'none';
      attachAIGenAssetContextualAttribute(_.extend({
        element,
        i18n: this.i18n,
        improveMMAOption: { type: 'Text' },
        newI18n: this.newI18n,
      }));
    });
  }

  updateAIGenAssetContextualAttributeStatus(field, display) {
    const aiGeneratedAssetContainer = $(`.${field}-AIGeneratedAsset`);

    if (!_.isEmpty(aiGeneratedAssetContainer) && aiGeneratedAssetContainer.length > 0) {
      _.first(aiGeneratedAssetContainer).style.display = display;
    }
  }

  getAIGenAssetContextualAttributeStatus(field) {
    const aiGeneratedAssetContainer = $(`.${field}-AIGeneratedAsset`);

    if (!_.isEmpty(aiGeneratedAssetContainer) && aiGeneratedAssetContainer.length > 0) {
      return _.first(aiGeneratedAssetContainer).style.display;
    }

    return null;
  }

  getBiddingSchemeType(options = {}) {
    if (this.isUpdate) {
      return _.get(options.ad, ['BiddingScheme', 'Type'], '');
    }
    return _.get(options, ['biddingScheme', 'Type'], '');
  }

  getVerifiedTrackingSettingAttachment(verifiedTrackingSettings = null) {
    if (_.isNull(verifiedTrackingSettings)) {
      return {};
    }

    if (_.isEmpty(verifiedTrackingSettings)) {
      return { VerifiedTrackingData: '' };
    }

    const result = [];
    _.mapObject(verifiedTrackingSettings, (value, key) => {
      result.push({ Key: key, Value: value });
    });

    return { VerifiedTrackingData: JSON.stringify([result]) };
  }
}
